import {
	BoatIcon,
	CalculatorIcon,
	ChatDotsIcon,
	CurrencyCircleDollarIcon,
	DatabaseIcon,
	GearIcon,
	InfoIcon,
	PresentationChartIcon,
	SidebarSimpleIcon,
	SignOutIcon,
} from "@phosphor-icons/react";
import { AnimatePresence, motion } from "motion/react";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router";

import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from "@/components/ui/sidebar";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { logout } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/useAuthStore";

const FeedbackModal = lazy(() =>
	import("@/components/features/feedback/FeedbackModal").then((m) => ({
		default: m.FeedbackModal,
	})),
);
const InformationModal = lazy(() =>
	import("@/components/features/feedback/InformationModal").then((m) => ({
		default: m.InformationModal,
	})),
);
const PrivacyModal = lazy(() =>
	import("@/components/features/legal/PrivacyModal").then((m) => ({
		default: m.PrivacyModal,
	})),
);
const TermsModal = lazy(() =>
	import("@/components/features/legal/TermsModal").then((m) => ({
		default: m.TermsModal,
	})),
);
const SettingsModal = lazy(() =>
	import("@/components/features/settings/SettingsModal").then((m) => ({
		default: m.SettingsModal,
	})),
);

const items = [
	{
		url: "/",
		icon: PresentationChartIcon,
		labelKey: "marketIntelligence.marketIntelligence",
	},
	{
		url: "/fleet-valuation",
		icon: BoatIcon,
		labelKey: "fleetValuation.fleetValuation",
	},
	{
		url: "/profitability-simulator",
		icon: CalculatorIcon,
		labelKey: "profitabilitySimulator.profitabilitySimulator",
	},
	{
		url: "/vessel-market-values",
		icon: CurrencyCircleDollarIcon,
		labelKey: "vesselMarketValues.vesselMarketValues",
	},
	{
		url: "/ship-database",
		icon: DatabaseIcon,
		labelKey: "shipDatabase.shipDatabase",
	},
	// V2 FEATURE - AI Intelligence
	// {
	// 	url: "/ai-intelligence",
	// 	icon: SparkleIcon,
	// 	labelKey: "aiIntelligence.aiIntelligence",
	// },
];

export function AppSidebar() {
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const { open, toggleSidebar } = useSidebar();
	const { t } = useTranslation();
	const user = useAuthStore((state) => state.user);
	const [feedbackOpen, setFeedbackOpen] = useState(false);
	const [infoOpen, setInfoOpen] = useState(false);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [termsOpen, setTermsOpen] = useState(false);
	const [privacyOpen, setPrivacyOpen] = useState(false);
	const displayName = user?.name || user?.email || "MarinaValue user";
	const displayEmail = user?.email || "";
	const avatarSrc = user?.picture_url || "/images/profile-pict.png";

	const handleLogout = async () => {
		await logout();
		navigate("/auth/login", { replace: true });
	};

	return (
		<Sidebar className="base-glassmorphism border-r-0! bg-gray-cool-150 dark:border dark:border-white/20">
			<SidebarContent className="w-full">
				<SidebarGroup className="rounded-none p-0">
					<SidebarGroupLabel
						className={`flex h-auto! items-center justify-between gap-3 rounded-none bg-white/5 p-4 backdrop-blur-sm dark:bg-transparent dark:backdrop-blur-none ${!open && "flex-col gap-y-4"}`}
					>
						<div className="flex items-center gap-3">
							<img src="/favicon.png" alt="logo" className="h-6 w-6" />
							<AnimatePresence>
								{open && (
									<motion.image
										initial={{ opacity: 0, width: 0 }}
										animate={{ opacity: 1, width: "auto" }}
										exit={{ opacity: 0, width: 0 }}
										transition={{ duration: 0.2 }}
										className="flex flex-col overflow-hidden leading-tight"
									>
										<img
											src="/icons/logo-text-marinavalue.svg"
											alt="logo-text"
											className="h-4"
										/>
									</motion.image>
								)}
							</AnimatePresence>
						</div>
						<button
							onClick={toggleSidebar}
							type="button"
							aria-label={open ? "Collapse sidebar" : "Expand sidebar"}
							className="hover:cursor-pointer"
						>
							<SidebarSimpleIcon
								aria-hidden
								className="text-end text-2xl text-gray-cool-500"
							/>
						</button>
					</SidebarGroupLabel>
					<SidebarGroupContent className="bg-transparent">
						<SidebarMenu>
							{items.map((item) => {
								const isActive =
									pathname === item.url ||
									(item.url !== "/" && pathname.startsWith(`${item.url}/`));
								const IconComponent = item.icon;
								return (
									<Tooltip key={item.url} open={open ? false : undefined}>
										<TooltipTrigger asChild>
											<SidebarMenuItem
												className={`flex justify-center ${isActive ? "border-marina-500 border-l-4" : ""}`}
											>
												<SidebarMenuButton
													asChild
													className={cn(
														"rounded-none py-6 transition-all duration-300 [&_svg]:size-6",
														isActive
															? "dark:border dark:border-white/15 dark:bg-white/10 dark:shadow-[0_0_20px_rgba(34,197,94,0.5)] dark:backdrop-blur-sm"
															: "hover:bg-gray-cool-100 hover:backdrop-blur-sm",
													)}
												>
													<Link
														to={item.url}
														aria-label={t(item.labelKey)}
														className={cn(
															"group flex items-center gap-3",
															!open && "justify-center",
															isActive && "bg-gray-cool-50",
														)}
													>
														<IconComponent
															aria-hidden
															className={cn(
																"shrink-0 scale-105 transition-colors duration-300 dark:text-white",
																isActive
																	? "text-marina-600 dark:text-indigo-100"
																	: "text-gray-cool-500 group-hover:text-marina-600 dark:group-hover:text-white",
															)}
														/>
														<AnimatePresence>
															{open && (
																<motion.span
																	initial={{ opacity: 0, width: 0 }}
																	animate={{ opacity: 1, width: "auto" }}
																	exit={{ opacity: 0, width: 0 }}
																	transition={{ duration: 0.2 }}
																	className={cn(
																		"overflow-hidden truncate font-semibold text-md dark:text-white",
																		isActive
																			? "font-bold text-marina-600 dark:text-indigo-100"
																			: "text-gray-cool-600 group-hover:font-semibold group-hover:text-marina-600 dark:group-hover:text-white",
																	)}
																>
																	{t(item.labelKey)}
																</motion.span>
															)}
														</AnimatePresence>
													</Link>
												</SidebarMenuButton>
											</SidebarMenuItem>
										</TooltipTrigger>
										{!open && (
											<TooltipContent
												side="right"
												className="bg-gray-800 text-[14px] text-white"
											>
												{t(item.labelKey)}
											</TooltipContent>
										)}
									</Tooltip>
								);
							})}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			</SidebarContent>
			<SidebarFooter className="w-full rounded-none px-0 pb-4">
				{/* Credit */}
				{open && (
					<div className="flex w-full items-center gap-x-2 border-gray-cool-200 border-y py-4">
						<p className="w-full text-center font-semibold text-gray-cool-500 text-xs">
							© 2026 MarinaValue Inc.
						</p>
					</div>
				)}
				<SidebarMenu className="flex flex-col items-center justify-center gap-y-3">
					{/* User profile */}
					<SidebarMenuItem className="w-full">
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<SidebarMenuButton className="group h-10 w-full rounded-lg pt-6 pb-8 hover:cursor-pointer hover:bg-gray-cool-100">
									{open ? (
										<div className="flex w-full items-center justify-start gap-x-1">
											<img
												src={avatarSrc}
												alt="user"
												className="size-8 rounded-full hover:cursor-pointer"
											/>
											<div className="flex min-w-0 flex-col items-start justify-start p-1">
												<p className="max-w-38 truncate font-bold text-gray-cool-800 text-md group-hover:dark:text-marina-400">
													{displayName}
												</p>
												<p className="max-w-38 truncate whitespace-nowrap text-gray-cool-600 text-xs group-hover:dark:text-marina-100/50">
													{displayEmail}
												</p>
											</div>
										</div>
									) : (
										<img
											src={avatarSrc}
											alt="user"
											className={cn(
												"size-8 rounded-full hover:cursor-pointer",
												!open && "ml-2",
											)}
										/>
									)}
								</SidebarMenuButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								side="right"
								align="end"
								sideOffset={16}
								className="min-w-50 -translate-y-2 rounded-xl border border-gray-cool-100 bg-white shadow-xl ring-0 dark:border-white/20 dark:bg-gray-800 [&>button]:absolute [&>button]:right-2 [&>button]:-translate-y-2 [&>button]:text-gray-500"
							>
								<div className="flex flex-col gap-y-3 px-3 py-3">
									<button
										type="button"
										onClick={() => setFeedbackOpen(true)}
										className="flex items-center gap-x-3 rounded-md px-2 py-1.5 text-left text-gray-cool-600 text-md hover:cursor-pointer hover:bg-gray-cool-100"
									>
										<ChatDotsIcon className="h-5 w-5 text-gray-cool-600" />
										<span className="font-semibold text-md">
											{t("userMenu.giveFeedback")}
										</span>
									</button>
									<button
										type="button"
										onClick={() => setInfoOpen(true)}
										className="flex items-center gap-x-3 rounded-md px-2 py-1.5 text-left text-gray-cool-600 text-md hover:cursor-pointer hover:bg-gray-cool-100"
									>
										<InfoIcon className="h-5 w-5 text-gray-cool-600" />
										<span className="font-semibold text-md">
											{t("userMenu.information")}
										</span>
									</button>
									<button
										type="button"
										onClick={() => setSettingsOpen(true)}
										className="flex items-center gap-x-3 rounded-md px-2 py-1.5 text-left text-gray-cool-600 text-md hover:cursor-pointer hover:bg-gray-cool-100"
									>
										<GearIcon className="h-5 w-5 text-gray-cool-600" />
										<span className="font-semibold text-md">
											{t("settings.settings")}
										</span>
									</button>
									<button
										type="button"
										onClick={handleLogout}
										className="flex items-center gap-x-3 rounded-md px-2 py-1.5 text-left text-gray-cool-600 text-md hover:cursor-pointer hover:bg-gray-cool-100"
									>
										<SignOutIcon className="h-5 w-5 text-gray-cool-600" />
										<span className="font-semibold text-md">
											{t("common.logOut")}
										</span>
									</button>
								</div>
								<div className="flex items-center justify-center gap-2 border-gray-200 border-t px-3 py-2 text-gray-cool-500 text-xs">
									<button
										type="button"
										onClick={() => setPrivacyOpen(true)}
										className="hover:text-gray-cool-700"
									>
										{t("common.privacy")}
									</button>
									<span className="text-gray-cool-400">|</span>
									<button
										type="button"
										onClick={() => setTermsOpen(true)}
										className="hover:text-gray-cool-700"
									>
										{t("common.terms")}
									</button>
								</div>
							</DropdownMenuContent>
						</DropdownMenu>
					</SidebarMenuItem>
				</SidebarMenu>
			</SidebarFooter>
			<Suspense fallback={null}>
				{feedbackOpen && (
					<FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
				)}
				{infoOpen && (
					<InformationModal open={infoOpen} onOpenChange={setInfoOpen} />
				)}
				{settingsOpen && (
					<SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
				)}
				{termsOpen && (
					<TermsModal open={termsOpen} onOpenChange={setTermsOpen} />
				)}
				{privacyOpen && (
					<PrivacyModal open={privacyOpen} onOpenChange={setPrivacyOpen} />
				)}
			</Suspense>
		</Sidebar>
	);
}
